import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiUsers, FiSettings, FiBook, FiBarChart3, FiArrowRight, FiCheckCircle, FiTrendingUp } = FiIcons;

const Dashboard = () => {
  const systemStats = [
    { label: 'SOPs Created', value: '12', icon: FiBook, bg: 'bg-skyblue-100', text: 'text-skyblue' },
    { label: 'Workflows Automated', value: '8', icon: FiSettings, bg: 'bg-sage-100', text: 'text-sage-600' },
    { label: 'Hours Saved/Week', value: '25', icon: FiTarget, bg: 'bg-lavender-100', text: 'text-lavender' },
    { label: 'ROI Expected', value: '340%', icon: FiTrendingUp, bg: 'bg-goldenrod-100', text: 'text-goldenrod' }
  ];

  const quickLinks = [
    { title: 'Executive Summary', description: 'System overview and ROI analysis', path: '/executive-summary', icon: FiTarget },
    { title: 'Role Breakdown', description: 'Coordinator responsibilities mapped', path: '/role-breakdown', icon: FiUsers },
    { title: 'System Architecture', description: 'Complete blueprint overview', path: '/architecture', icon: FiSettings },
    { title: 'Core Modules', description: 'Detailed workflow modules', path: '/modules', icon: FiBook }
  ];

  const recentUpdates = [
    { title: 'SOP Library Completed', description: '12 standard operating procedures ready', time: '2 hours ago', status: 'completed' },
    { title: 'Dashboard Templates Created', description: 'Marketing coordinator command center', time: '4 hours ago', status: 'completed' },
    { title: 'Implementation Timeline', description: '1-2 week deployment plan finalized', time: '6 hours ago', status: 'completed' },
    { title: 'Expansion Opportunities', description: 'Upsell packages identified', time: '8 hours ago', status: 'completed' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className="text-4xl font-bold text-charcoal mb-3">
          Web Marketing Coordinator System
        </h1>
        <p className="text-xl text-charcoal-500 font-light">
          MindGarden Edition <span className="mx-2">•</span> Complete SOP & Workflow Solution
        </p>
      </motion.div>

      {/* System Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
      >
        {systemStats.map((stat, index) => (
          <div key={stat.label} className="glass-card rounded-xl p-6 hover-lift border-l-4 border-sage">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className={`w-6 h-6 ${stat.text}`} />
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-charcoal">{stat.value}</div>
                <div className="text-xs font-semibold text-charcoal-500 uppercase tracking-wide">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <h2 className="text-xl font-bold text-charcoal mb-6 flex items-center">
            <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2 text-sage" />
            System Components
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className="glass-card rounded-xl p-6 hover-lift group transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-sand-200 rounded-xl flex items-center justify-center group-hover:bg-sage-100 transition-colors">
                    <SafeIcon icon={link.icon} className="w-6 h-6 text-charcoal group-hover:text-sage-600 transition-colors" />
                  </div>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-charcoal-500 group-hover:text-sage-600 transition-colors" />
                </div>
                <h3 className="font-bold text-charcoal mb-2">{link.title}</h3>
                <p className="text-sm text-charcoal-500">{link.description}</p>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Recent Updates */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-xl font-bold text-charcoal mb-6 flex items-center">
             <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2 text-sage" />
             System Status
          </h2>
          <div className="glass-card rounded-xl p-6">
            <div className="space-y-6">
              {recentUpdates.map((update, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-sage-50 rounded-full flex items-center justify-center flex-shrink-0 border border-sage-100">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-sage" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-charcoal">{update.title}</p>
                    <p className="text-xs text-charcoal-500">{update.description}</p>
                    <p className="text-[10px] font-semibold text-sage-600 mt-1 uppercase tracking-wider">{update.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-12 text-center"
      >
        <div className="glass-card rounded-2xl p-10 mindgarden-gradient relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Marketing Operations?</h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg">
              This comprehensive system will streamline your web marketing coordinator workflows, reduce manual tasks by 70%, and increase team productivity by 340%.
            </p>
            <Link
              to="/executive-summary"
              className="inline-flex items-center px-8 py-4 bg-white text-sage-800 rounded-xl font-bold hover:bg-sand-50 transition-colors shadow-lg"
            >
              Start Exploring
              <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-3" />
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;